import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import Pagination from "../components/Pagination/Pagination";
import * as studentsService from "../services/students";
import * as lessonsService from "../services/lessons";
import TeacherStudents from "./TeacherStudents";
import "./Students.css";
import { materializeLessonForReference } from "../utils/lessonOccurrences";

const EMPTY_STUDENT = {
  first_name: "",
  last_name: "",
  date_of_birth: "",
  password: "",
  notes: "",
};

function formatDate(value, locale = undefined) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString(locale);
  } catch (_error) {
    return value;
  }
}

function formatDateTime(value, locale = undefined) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString(locale);
  } catch (_error) {
    return value;
  }
}

// Teachers get their own "My students" view; this page is the parents' one.
export default function Students() {
  const { user, effectiveRole } = useAuth();
  if (user?.role === "teacher" && effectiveRole === "teacher") return <TeacherStudents />;
  return <ParentStudents />;
}

function ParentStudents() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, accessToken, activeStudentId, setActiveStudent } = useAuth();
  const { t, language } = useTranslation();
  const firstNameInputRef = useRef(null);

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const isAddFlow = query.get("add") === "1";
  const returnTarget = query.get("return");
  const dateLocale =
    language === "ar"
      ? "ar-TN"
      : language === "fr"
        ? "fr-FR"
        : language === "de"
          ? "de-DE"
          : language === "it"
            ? "it-IT"
            : language === "es"
              ? "es-ES"
              : "en-US";
  const isParent = user?.role === "parent";

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsTotalPages, setStudentsTotalPages] = useState(1);
  const [studentForm, setStudentForm] = useState(EMPTY_STUDENT);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [studentSaving, setStudentSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [showStudentPassword, setShowStudentPassword] = useState(false);

  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);

  const loadStudents = useCallback(
    async (page = 1) => {
      if (!accessToken || !isParent) {
        return;
      }
      setStudentsLoading(true);
      setStudentsError(null);
      try {
        const payload = await studentsService.fetchStudents({
          accessToken,
          page,
        });
        const list = payload.students || [];
        setStudents(list);
        setStudentsPage(payload.page || page || 1);
        setStudentsTotalPages(payload.total_pages || 1);

        if (
          !selectedStudentId ||
          !list.some((student) => student.id === selectedStudentId)
        ) {
          setSelectedStudentId(list.length ? list[0].id : null);
        }

        // If a parent previously enabled student mode for a child who no longer exists,
        // clear it instead of switching to another child implicitly.
        if (
          activeStudentId &&
          !list.some((student) => student.id === activeStudentId)
        ) {
          setActiveStudent(null);
        }
      } catch (error) {
        setStudentsError(error.message);
        setStudents([]);
        setStudentsPage(1);
        setStudentsTotalPages(1);
        setSelectedStudentId(null);
        setActiveStudent(null);
      } finally {
        setStudentsLoading(false);
      }
    },
    [
      accessToken,
      isParent,
      activeStudentId,
      selectedStudentId,
      setActiveStudent,
    ],
  );

  const loadLessons = useCallback(async () => {
    if (!accessToken || !isParent) {
      return;
    }
    setLessonsLoading(true);
    try {
      const payload = await lessonsService.fetchLessons({ accessToken });
      setLessons(payload.lessons || []);
    } catch (_error) {
      setLessons([]);
    } finally {
      setLessonsLoading(false);
    }
  }, [accessToken, isParent]);

  useEffect(() => {
    loadStudents(1);
  }, [loadStudents]);

  useEffect(() => {
    loadLessons();
  }, [loadLessons]);

  const handleStudentFieldChange = (event) => {
    const { name, value } = event.target;
    setStudentForm((current) => ({ ...current, [name]: value }));
  };

  const resetForm = useCallback(() => {
    setStudentForm(EMPTY_STUDENT);
    setEditingStudentId(null);
  }, []);

  useEffect(() => {
    if (!isAddFlow) {
      return;
    }
    resetForm();
    setFeedback(null);
    setStudentsError(null);

    const timer = window.setTimeout(() => {
      firstNameInputRef.current?.focus?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isAddFlow, resetForm]);

  const handleStudentSubmit = async (event) => {
    event.preventDefault();
    if (!accessToken || !isParent) {
      return;
    }
    setStudentSaving(true);
    setStudentsError(null);
    setFeedback(null);
    try {
      const payload = {
        first_name: studentForm.first_name.trim(),
        last_name: studentForm.last_name.trim(),
        notes: studentForm.notes.trim(),
        date_of_birth: studentForm.date_of_birth || null,
      };
      if (!payload.first_name) {
        throw new Error(t("studentsPage.errors.firstNameRequired"));
      }

      if (!editingStudentId) {
        const password = studentForm.password || "";
        if (!password) {
          throw new Error(t("studentsPage.errors.passwordRequired"));
        }
        if (password.length < 8) {
          throw new Error(t("studentsPage.errors.passwordMin"));
        }
        payload.password = password;
      }

      if (editingStudentId) {
        const updated = await studentsService.updateStudent(
          accessToken,
          editingStudentId,
          payload,
        );
        setStudents((list) =>
          list.map((student) =>
            student.id === editingStudentId
              ? { ...student, ...updated }
              : student,
          ),
        );
        setSelectedStudentId(editingStudentId);
        setFeedback(t("studentsPage.feedback.updated"));
      } else {
        const created = await studentsService.createStudent(
          accessToken,
          payload,
        );
        setStudents((list) => [created, ...list]);
        setSelectedStudentId(created.id);

        if (created?.login_email) {
          setFeedback(
            t("studentsPage.feedback.createdWithLogin", {
              email: created.login_email,
            }),
          );
        } else {
          setFeedback(t("studentsPage.feedback.created"));
        }

        if (isAddFlow && returnTarget === "dashboard") {
          navigate("/dashboard", { replace: true });
          return;
        }
      }
      await loadLessons();
      resetForm();
    } catch (error) {
      setStudentsError(error.message);
    } finally {
      setStudentSaving(false);
    }
  };

  const handleStudentEdit = (student) => {
    setStudentForm({
      first_name: student.first_name,
      last_name: student.last_name,
      date_of_birth: student.date_of_birth || "",
      password: "",
      notes: student.notes || "",
    });
    setEditingStudentId(student.id);
    setFeedback(null);
  };

  const handleStudentDelete = async (studentId) => {
    if (!accessToken || !isParent) {
      return;
    }
    const confirmed = window.confirm(t("studentsPage.confirmDelete"));
    if (!confirmed) {
      return;
    }
    try {
      await studentsService.deleteStudent(accessToken, studentId);
      setStudents((list) => list.filter((student) => student.id !== studentId));
      setFeedback(t("studentsPage.feedback.deleted"));

      if (selectedStudentId === studentId) {
        const remaining = students.filter(
          (student) => student.id !== studentId,
        );
        setSelectedStudentId(remaining.length ? remaining[0].id : null);
      }

      // If the deleted student was active in student mode, exit student mode.
      if (activeStudentId === studentId) {
        setActiveStudent(null);
      }

      await loadLessons();
    } catch (error) {
      setStudentsError(error.message);
    }
  };

  const lessonsByStudentId = useMemo(() => {
    return lessons.reduce((accumulator, lesson) => {
      const studentId = lesson.student?.id;
      if (!studentId) {
        return accumulator;
      }
      if (!accumulator[studentId]) {
        accumulator[studentId] = [];
      }
      accumulator[studentId].push(lesson);
      return accumulator;
    }, {});
  }, [lessons]);

  const selectedStudent = useMemo(() => {
    if (!selectedStudentId) {
      return null;
    }
    return students.find((student) => student.id === selectedStudentId) || null;
  }, [students, selectedStudentId]);

  const selectedLessons = useMemo(() => {
    if (!selectedStudent) {
      return [];
    }
    const list = lessonsByStudentId[selectedStudent.id] || [];
    return [...list].sort((a, b) => {
      const aTime = a.scheduled_at ? new Date(a.scheduled_at).getTime() : 0;
      const bTime = b.scheduled_at ? new Date(b.scheduled_at).getTime() : 0;
      return aTime - bTime;
    });
  }, [lessonsByStudentId, selectedStudent]);

  const upcomingLessons = useMemo(() => {
    const referenceDate = new Date();
    const referenceTs = referenceDate.getTime();

    return (selectedLessons || [])
      .map((lesson) => {
        const materialized = materializeLessonForReference(
          lesson,
          referenceDate,
        );
        if (!materialized?.scheduled_at) return null;
        const startTs = new Date(materialized.scheduled_at).getTime();
        if (!Number.isFinite(startTs)) return null;

        const durationMinutes = Number(materialized.duration_minutes) || 60;
        const endTs = startTs + durationMinutes * 60 * 1000;
        if (endTs < referenceTs) return null;

        return { lesson: materialized, startTs };
      })
      .filter(Boolean)
      .sort((a, b) => a.startTs - b.startTs)
      .slice(0, 4)
      .map((item) => item.lesson);
  }, [selectedLessons]);

  const completedCount = useMemo(() => {
    return selectedLessons.filter((lesson) => lesson.status === "completed")
      .length;
  }, [selectedLessons]);

  const cancelledCount = useMemo(() => {
    return selectedLessons.filter((lesson) => lesson.status === "cancelled")
      .length;
  }, [selectedLessons]);

  if (!isParent) {
    return (
      <div className="students-page">
        <div className="students-shell">
          <header className="students-head">
            <h1>{t("studentsPage.noAccess.title")}</h1>
            <p className="students-muted">{t("studentsPage.noAccess.lead")}</p>
          </header>
          <div className="students-panel">
            <p className="students-muted">{t("studentsPage.noAccess.body")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="students-page">
      <div className="students-shell">
        <header className="students-head">
          <div>
            <h1>{t("studentsPage.title")}</h1>
            <p className="students-muted">{t("studentsPage.lead")}</p>
            <p className="students-muted" style={{ marginTop: "0.4rem" }}>
              {t("studentsPage.leadStudentMode")}
            </p>
          </div>
        </header>

        <div className="students-layout">
          <section className="students-panel students-panel--form">
            <h2 className="students-title">
              {editingStudentId
                ? t("studentsPage.form.editTitle")
                : t("studentsPage.form.newTitle")}
            </h2>
            <form className="students-form" onSubmit={handleStudentSubmit}>
              <div className="students-grid">
                <div className="students-field">
                  <label htmlFor="student-first-name">
                    {t("studentsPage.form.firstName")}
                  </label>
                  <input
                    id="student-first-name"
                    name="first_name"
                    type="text"
                    value={studentForm.first_name}
                    onChange={handleStudentFieldChange}
                    placeholder={t("studentsPage.placeholders.firstName")}
                    disabled={studentSaving}
                    ref={firstNameInputRef}
                    required
                  />
                </div>
                <div className="students-field">
                  <label htmlFor="student-last-name">
                    {t("studentsPage.form.lastName")}
                  </label>
                  <input
                    id="student-last-name"
                    name="last_name"
                    type="text"
                    value={studentForm.last_name}
                    onChange={handleStudentFieldChange}
                    placeholder={t("studentsPage.placeholders.lastName")}
                    disabled={studentSaving}
                  />
                </div>
                <div className="students-field">
                  <label htmlFor="student-dob">
                    {t("studentsPage.form.dob")}
                  </label>
                  <input
                    id="student-dob"
                    name="date_of_birth"
                    type="date"
                    value={studentForm.date_of_birth}
                    onChange={handleStudentFieldChange}
                    disabled={studentSaving}
                  />
                </div>

                {!editingStudentId && (
                  <div className="students-field">
                    <label htmlFor="student-password">
                      {t("studentsPage.form.password")}
                    </label>
                    <div className="password-wrap">
                      <input
                        id="student-password"
                        name="password"
                        type={showStudentPassword ? "text" : "password"}
                        value={studentForm.password}
                        onChange={handleStudentFieldChange}
                        placeholder={t("studentsPage.placeholders.password")}
                        disabled={studentSaving}
                        required
                        minLength={8}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() =>
                          setShowStudentPassword((current) => !current)
                        }
                        aria-label={
                          showStudentPassword
                            ? t("authPage.hidePassword")
                            : t("authPage.showPassword")
                        }
                        title={
                          showStudentPassword
                            ? t("authPage.hidePassword")
                            : t("authPage.showPassword")
                        }
                        disabled={studentSaving}
                      >
                        <i
                          className={`fa-solid ${showStudentPassword ? "fa-eye-slash" : "fa-eye"}`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="students-field">
                <label htmlFor="student-notes">
                  {t("studentsPage.form.notes")}
                </label>
                <textarea
                  id="student-notes"
                  name="notes"
                  value={studentForm.notes}
                  onChange={handleStudentFieldChange}
                  placeholder={t("studentsPage.placeholders.notes")}
                  disabled={studentSaving}
                  rows={3}
                />
              </div>
              {studentsError && (
                <p className="students-error">{studentsError}</p>
              )}
              {feedback && <p className="students-feedback">{feedback}</p>}
              <div className="students-actions">
                <button
                  type="submit"
                  className="btn primary"
                  disabled={studentSaving}
                >
                  {studentSaving
                    ? t("studentsPage.actions.saving")
                    : editingStudentId
                      ? t("studentsPage.actions.update")
                      : t("studentsPage.actions.create")}
                </button>
                {editingStudentId && (
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      resetForm();
                      setFeedback(null);
                    }}
                    disabled={studentSaving}
                  >
                    {t("studentsPage.actions.cancel")}
                  </button>
                )}
              </div>
            </form>
          </section>

          <section className="students-panel students-panel--list">
            <div className="students-list-head">
              <h2 className="students-title">{t("studentsPage.list.title")}</h2>
              <button
                type="button"
                className="students-refresh"
                onClick={() => loadStudents(studentsPage)}
                disabled={studentsLoading}
              >
                {studentsLoading ? "..." : t("studentsPage.list.refresh")}
              </button>
            </div>

            {studentsLoading ? (
              <p className="students-muted">{t("studentsPage.list.loading")}</p>
            ) : students.length ? (
              <ul className="students-list">
                {students.map((student) => {
                  const isActive = student.id === selectedStudentId;
                  const studentLessons = lessonsByStudentId[student.id] || [];
                  const upcomingCount = (() => {
                    const referenceDate = new Date();
                    const referenceTs = referenceDate.getTime();
                    return studentLessons.reduce((count, lesson) => {
                      const materialized = materializeLessonForReference(
                        lesson,
                        referenceDate,
                      );
                      const scheduledAt = materialized?.scheduled_at;
                      if (!scheduledAt) return count;
                      const startTs = new Date(scheduledAt).getTime();
                      if (!Number.isFinite(startTs)) return count;
                      const durationMinutes =
                        Number(materialized.duration_minutes) || 60;
                      const endTs = startTs + durationMinutes * 60 * 1000;
                      if (endTs < referenceTs) return count;
                      return count + 1;
                    }, 0);
                  })();

                  return (
                    <li
                      key={student.id}
                      className={`students-card${isActive ? " is-active" : ""}`}
                    >
                      <button
                        type="button"
                        className="students-card__body"
                        onClick={() => setSelectedStudentId(student.id)}
                      >
                        <span className="students-card__name">
                          {student.full_name}
                        </span>
                        <span className="students-card__meta">
                          {upcomingCount
                            ? t("studentsPage.card.upcoming", {
                                count: upcomingCount,
                              })
                            : t("studentsPage.card.noUpcoming")}
                        </span>
                        {student.date_of_birth && (
                          <span className="students-card__meta">
                            {t("studentsPage.card.dob", {
                              date: formatDate(
                                student.date_of_birth,
                                dateLocale,
                              ),
                            })}
                          </span>
                        )}
                      </button>
                      <div className="students-card__actions">
                        <button
                          type="button"
                          className="students-card__action"
                          onClick={() => handleStudentEdit(student)}
                        >
                          {t("studentsPage.card.edit")}
                        </button>
                        <button
                          type="button"
                          className="students-card__action"
                          onClick={() => {
                            setActiveStudent(student.id);
                            navigate("/dashboard");
                          }}
                        >
                          {t("studentsPage.card.enterAs")}
                        </button>
                        <button
                          type="button"
                          className="students-card__action students-card__action--danger"
                          onClick={() => handleStudentDelete(student.id)}
                        >
                          {t("studentsPage.card.delete")}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="students-empty">{t("studentsPage.list.empty")}</p>
            )}

            <Pagination
              page={studentsPage}
              totalPages={studentsTotalPages}
              dir={language === "ar" ? "rtl" : "ltr"}
              disabled={studentsLoading}
              onPageChange={(nextPage) => loadStudents(nextPage)}
            />
          </section>
        </div>

        {selectedStudent && (
          <section className="students-panel students-panel--detail">
            <header className="students-detail-head">
              <div>
                <h2 className="students-title">
                  {t("studentsPage.detail.title", {
                    name: selectedStudent.full_name,
                  })}
                </h2>
                <p className="students-muted">
                  {t("studentsPage.detail.lead")}
                </p>
              </div>
              {lessonsLoading && (
                <span className="students-muted">
                  {t("studentsPage.detail.loadingLessons")}
                </span>
              )}
              <div className="students-actions" style={{ marginTop: "0.4rem" }}>
                <button
                  type="button"
                  className={`btn ${
                    activeStudentId === selectedStudent.id ? "primary" : "ghost"
                  }`}
                  onClick={() => {
                    setActiveStudent(selectedStudent.id);
                    navigate("/dashboard");
                  }}
                >
                  {activeStudentId === selectedStudent.id
                    ? t("studentsPage.detail.active")
                    : t("studentsPage.detail.activate")}
                </button>
              </div>
            </header>

            <div className="students-detail-grid">
              <div className="students-detail-card">
                <span className="students-tag">
                  {t("studentsPage.detail.stats.total")}
                </span>
                <strong>{selectedLessons.length}</strong>
              </div>
              <div className="students-detail-card">
                <span className="students-tag">
                  {t("studentsPage.detail.stats.completed")}
                </span>
                <strong>{completedCount}</strong>
              </div>
              <div className="students-detail-card">
                <span className="students-tag">
                  {t("studentsPage.detail.stats.cancelled")}
                </span>
                <strong>{cancelledCount}</strong>
              </div>
            </div>

            <div className="students-detail-box">
              <h3>{t("studentsPage.detail.info.title")}</h3>
              <ul>
                <li>
                  <span className="students-label">
                    {t("studentsPage.detail.info.name")}
                  </span>
                  <span>{selectedStudent.full_name}</span>
                </li>
                <li>
                  <span className="students-label">
                    {t("studentsPage.detail.info.dob")}
                  </span>
                  <span>
                    {formatDate(selectedStudent.date_of_birth, dateLocale)}
                  </span>
                </li>
                <li>
                  <span className="students-label">
                    {t("studentsPage.detail.info.notes")}
                  </span>
                  <span>
                    {selectedStudent.notes ||
                      t("studentsPage.detail.info.noNotes")}
                  </span>
                </li>
              </ul>
            </div>

            <div className="students-detail-box">
              <h3>{t("studentsPage.detail.upcoming.title")}</h3>
              {upcomingLessons.length ? (
                <ul className="students-lessons">
                  {upcomingLessons.map((lesson) => (
                    <li key={lesson.id}>
                      <p className="students-lesson__title">{lesson.title}</p>
                      <p className="students-lesson__meta">
                        {t("studentsPage.detail.upcoming.time", {
                          value: formatDateTime(
                            lesson.scheduled_at,
                            dateLocale,
                          ),
                        })}
                      </p>
                      <p className="students-lesson__meta">
                        {t("studentsPage.detail.upcoming.duration", {
                          value: lesson.duration_minutes,
                        })}
                      </p>
                      <p className="students-lesson__meta">
                        {t("studentsPage.detail.upcoming.teacher", {
                          value:
                            lesson.teacher?.full_name || lesson.teacher?.email,
                        })}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="students-empty">
                  {t("studentsPage.detail.upcoming.empty")}
                </p>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
